import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Trash2, AlertTriangle, RefreshCw, PlayCircle } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { formatDateTimeIST } from '../../lib/timeUtils';

function CleanupAttempts() {
  const [stuckAttempts, setStuckAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchStuckAttempts();
  }, []);

  const fetchStuckAttempts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/stuck-attempts');
      setStuckAttempts(response.data);
    } catch (error) {
      console.error('Error fetching stuck attempts:', error);
      toast.error('Failed to fetch stuck attempts');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async (attemptId) => {
    if (!window.confirm('Are you sure you want to delete this stuck attempt?')) return;

    setDeleting(true);
    try {
      await api.delete(`/admin/stuck-attempts/${attemptId}`);
      toast.success('Attempt deleted successfully');
      fetchStuckAttempts();
    } catch (error) {
      console.error('Error deleting attempt:', error);
      toast.error('Failed to delete attempt');
    } finally {
      setDeleting(false);
    }
  };

  const handleAllowResume = async (attemptId) => {
    try {
      await api.post(`/admin/stuck-attempts/${attemptId}/allow-resume`);
      toast.success('Resume allowed — student can now continue the test');
      fetchStuckAttempts();
    } catch (error) {
      console.error('Error allowing resume:', error);
      toast.error('Failed to allow resume');
    }
  };

  const handleCleanupAll = async () => {
    if (!window.confirm(`Are you sure you want to delete all ${stuckAttempts.length} stuck attempts? This action cannot be undone.`)) return;

    setDeleting(true);
    try {
      await api.post('/admin/stuck-attempts/cleanup-all');
      toast.success(`Deleted ${stuckAttempts.length} stuck attempts`);
      fetchStuckAttempts();
    } catch (error) {
      console.error('Error cleaning up attempts:', error);
      toast.error('Failed to cleanup attempts');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-64'>
          <div className='spinner'></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>Cleanup Stuck Attempts</h1>
            <p className='text-gray-600'>Manage abandoned test attempts that are stuck in progress</p>
          </div>
          <div className='flex gap-2'>
            <Button
              variant='outline'
              onClick={fetchStuckAttempts}
              disabled={loading}
            >
              <RefreshCw className='w-4 h-4 mr-2' />
              Refresh
            </Button>
            {stuckAttempts.length > 0 && (
              <Button
                variant='destructive'
                onClick={handleCleanupAll}
                disabled={deleting}
              >
                <Trash2 className='w-4 h-4 mr-2' />
                Cleanup All ({stuckAttempts.length})
              </Button>
            )}
          </div>
        </div>

        {stuckAttempts.length === 0 ? (
          <Card className='p-12 text-center'>
            <AlertTriangle className='w-16 h-16 mx-auto mb-4 text-green-500' />
            <h3 className='text-xl font-semibold mb-2'>No Stuck Attempts Found</h3>
            <p className='text-gray-600'>All test attempts are in proper state. Great job!</p>
          </Card>
        ) : (
          <Card className='overflow-hidden'>
            <div className='bg-yellow-50 border-b border-yellow-200 p-4'>
              <div className='flex items-center'>
                <AlertTriangle className='w-5 h-5 text-yellow-600 mr-2' />
                <p className='text-sm text-yellow-800'>
                  Found {stuckAttempts.length} stuck attempt{stuckAttempts.length > 1 ? 's' : ''}. 
                  These are test attempts that were started but never completed or terminated.
                </p>
              </div>
            </div>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      User
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Test
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Started
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Duration
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Status
                    </th>
                    <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {stuckAttempts.map((attempt) => {
                    const startTime = new Date(attempt.start_time);
                    const now = new Date();
                    const hoursSinceStart = Math.floor((now - startTime) / (1000 * 60 * 60));
                    
                    return (
                      <tr key={attempt.id}>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='text-sm font-medium text-gray-900'>{attempt.user_name}</div>
                          <div className='text-sm text-gray-500'>{attempt.user_email}</div>
                        </td>
                        <td className='px-6 py-4'>
                          <div className='text-sm text-gray-900'>{attempt.test_title}</div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='text-sm text-gray-900'>{formatDateTimeIST(attempt.start_time)}</div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <Badge variant={hoursSinceStart > 24 ? 'destructive' : 'warning'}>
                            {hoursSinceStart}h ago
                          </Badge>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <Badge variant='warning'>In Progress</Badge>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-right'>
                          <div className='flex items-center justify-end gap-1'>
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() => handleAllowResume(attempt.id)}
                              disabled={deleting}
                              title="Allow student to resume this test"
                              data-testid={`allow-resume-${attempt.id}`}
                            >
                              <PlayCircle className='w-4 h-4 text-green-600 mr-1' />
                              Allow Resume
                            </Button>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => handleCleanup(attempt.id)}
                              disabled={deleting}
                              title="Delete this stuck attempt"
                            >
                              <Trash2 className='w-4 h-4 text-red-600' />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}

export default CleanupAttempts;